import { useState } from 'react'
import { X, CheckCircle2 } from 'lucide-react'
import { useRegisterBet } from '@/hooks/usePersonalBankroll'
import { usePersonalBankroll } from '@/hooks/usePersonalBankroll'
import { formatOdds } from '@/lib/utils'

interface RegisterBetModalProps {
  onClose: () => void
  category?: string
  matchLabel: string
  selectionLabel: string
  // Sprint "Marcadores Probables V2" (05/09/2026, Sección 3/4 del encargo): `null` es un
  // valor real -- "John Anthony no tenía cuota real para esto" (típico de un Marcador
  // Probable). Nunca se fabrica un 0.0/placeholder aquí ni en el backend.
  referenceOdds: number | null
  decisionRecordId?: number
  challengeStepId?: number
  parlayId?: number
  // Sprint "Mi Banca V2" (10/09/2026, Sección 1/2 del encargo): 3 orígenes nuevos -- JA Lab/
  // Parlay Lab/SGP Lab reutilizan este MISMO modal, nunca uno propio. `market` solo se usa
  // (y se exige) junto a `jaLabUsageId` -- desambigua cuál de las hasta 3 recomendaciones
  // reales de esa ejecución se está jugando.
  jaLabUsageId?: number
  parlayLabRunId?: number
  sgpLabRunId?: number
  market?: string
}

/** Fase R.11 (Bloque G/H, 17/08/2026): "LA JUGUÉ" -- desde cualquier recomendación oficial de
 * John Anthony (o el pick del Reto semanal), el usuario registra su apuesta REAL. Prellena la
 * cuota de referencia pero SIEMPRE permite editarla (la cuota real conseguida puede ser
 * distinta) y el stake (banca monetaria como fuente de verdad, Bloque H). Si Mi Banca
 * Personal todavía no existe, lo indica explícitamente en vez de fallar en silencio.
 *
 * Sprint "Marcadores Probables V2" (05/09/2026, Sección 4/8 del encargo): se reutiliza este
 * MISMO modal (nunca una arquitectura de registro aparte) para Marcadores Probables, con dos
 * añadidos aditivos: `referenceOdds` puede ser `null` (Marcadores Probables no exige cuota
 * real) y un campo opcional "Casa de apuestas" (`bookmaker`). */
export function RegisterBetModal({
  onClose,
  category,
  matchLabel,
  selectionLabel,
  referenceOdds,
  decisionRecordId,
  challengeStepId,
  parlayId,
  jaLabUsageId,
  parlayLabRunId,
  sgpLabRunId,
  market,
}: RegisterBetModalProps) {
  const { data: bankroll } = usePersonalBankroll()
  const [playedOdds, setPlayedOdds] = useState(referenceOdds != null ? String(referenceOdds) : '')
  const [stake, setStake] = useState('')
  const [bookmaker, setBookmaker] = useState('')
  const register = useRegisterBet()

  const parsedOdds = Number(playedOdds.replace(',', '.'))
  const parsedStake = Number(stake.replace(',', '.'))
  const canSubmit = parsedOdds > 1.0 && parsedStake > 0 && !register.isPending

  if (register.isSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4" onClick={onClose}>
        <div className="card w-full max-w-sm p-6 text-center space-y-3" onClick={(e) => e.stopPropagation()}>
          <CheckCircle2 className="h-10 w-10 mx-auto text-brand" />
          <p className="text-sm font-black text-text">¡Apuesta registrada!</p>
          <p className="text-xs text-text-dim">Ya la puedes ver en Mi Banca Personal → Mis Apuestas.</p>
          <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-brand text-black text-xs font-black uppercase">
            Cerrar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-sm p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-black text-text">LA JUGUÉ</p>
          <button onClick={onClose} className="p-1 text-text-dim hover:text-text">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="bg-panel-elevated/50 p-3 rounded-xl border border-border/40 space-y-1">
          <p className="text-xs font-bold text-text">{matchLabel}</p>
          <p className="text-[11px] text-text-dim">{selectionLabel}</p>
          <p className="text-[10px] text-text-dim">
            {referenceOdds != null ? (
              <>Cuota John Anthony: <span className="font-mono font-bold text-text">@{formatOdds(referenceOdds)}</span></>
            ) : (
              'John Anthony no tenía cuota real disponible para esto -- igual puedes registrar tu jugada.'
            )}
          </p>
        </div>

        {!bankroll?.configured && (
          <p className="text-[11px] text-warn bg-warn-soft/10 border border-warn/20 rounded-lg p-2">
            Todavía no creaste Mi Banca Personal. Ve a "Mi Banca Personal" en el menú Más para configurarla primero.
          </p>
        )}

        <div className="space-y-2">
          {/* P0.53 (29/08/2026, Sección 13/18 del encargo): "Mi cuota" -- la única cuota
              editable aquí. Prellenada con la cuota John Anthony por comodidad, pero el
              usuario puede (y a menudo debe) cambiarla por la que consiguió de verdad. Nunca
              sobrescribe `referenceOdds`, que se muestra arriba, fija. */}
          <label className="text-[10px] font-bold uppercase text-text-dim">Mi cuota (la que conseguiste tú)</label>
          <input
            type="number" inputMode="decimal" step="0.01" min="1.01" value={playedOdds}
            onChange={(e) => setPlayedOdds(e.target.value)}
            className="w-full bg-panel-elevated border border-border/60 rounded-xl px-3 py-2.5 text-sm font-mono font-bold text-text focus:outline-none focus:border-brand/50"
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase text-text-dim">Stake (€)</label>
          <input
            type="number" inputMode="decimal" step="0.01" min="0.01" placeholder="0.00" value={stake}
            onChange={(e) => setStake(e.target.value)}
            className="w-full bg-panel-elevated border border-border/60 rounded-xl px-3 py-2.5 text-sm font-mono font-bold text-text focus:outline-none focus:border-brand/50"
          />
        </div>
        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase text-text-dim">Casa de apuestas (opcional)</label>
          <input
            type="text" placeholder="p. ej. Bet365" value={bookmaker}
            onChange={(e) => setBookmaker(e.target.value)}
            className="w-full bg-panel-elevated border border-border/60 rounded-xl px-3 py-2.5 text-sm font-bold text-text focus:outline-none focus:border-brand/50"
          />
        </div>

        {register.isError && (
          <p className="text-[10px] text-danger">No se pudo registrar la apuesta. Intenta de nuevo.</p>
        )}

        <button
          disabled={!canSubmit || !bankroll?.configured}
          onClick={() =>
            register.mutate({
              decision_record_id: decisionRecordId,
              challenge_step_id: challengeStepId,
              parlay_id: parlayId,
              ja_lab_usage_id: jaLabUsageId,
              parlay_lab_run_id: parlayLabRunId,
              sgp_lab_run_id: sgpLabRunId,
              market,
              category,
              played_odds: parsedOdds,
              stake: parsedStake,
              bookmaker: bookmaker.trim() || undefined,
            })
          }
          className="w-full py-2.5 rounded-xl bg-brand text-black text-xs font-black uppercase tracking-wider disabled:opacity-40 disabled:pointer-events-none"
        >
          Confirmar
        </button>
      </div>
    </div>
  )
}
